import React, { useState, useEffect } from "react";
import { Typography, Grid, Box, Button } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import DeleteIcon from "../../../assets/icons/delete-red.svg";

import TextFieldInput from "../../../components/TextFieldInput";
import CustomDatePicker from "../../../components/CustomDatePicker";
import {
  APPLICANT_NUMBER_WORD,
  DATE_START_YEAR,
} from "../../../config/constant";
import {
  setActiveStep,
  setApplicantActiveStep,
  setApplicantTotal,
  setEditPreviewApplicant,
  setStepsCompleted,
  updateApplicantDetailsForm,
  deleteApplicantDetailsForm,
} from "../voucher-form.slice";
import { route } from "../../../services/apiRoutes";
import CommonButtonVoucherForm from "../../../shared-components/common-button/CommonButtonVoucherForm";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import AddApplicantButton from "./AddApplicantButton";
import DeleteConfirmationModal from "../../../shared-components/delete-confirmation-modal/DeleteConfirmationModal";
import { RHFPhoneInput } from "../../../shared-components/phone-input/RHFPhoneInput";
import { isValidPhoneNumberWithRules } from "../../../utilis/common-funtions";

const OtherApplicantDetails = ({ applicantNo }) => {
  const dispatch = useDispatch();
  const { voucherId } = useParams();
  const history = useHistory();

  const [applicantObject, setApplicantObject] = useState("");

  const {
    activeStep,
    applicantData,
    stepsCompleted,
    enquiryDetails,
    EOIBooking,
    applicantTotal,
  } = useSelector((state) => state.voucherForm);

  const [snackbarConfig, setSnackBarconfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const otherDetailFormik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      dob: "",
      countryCode: "IN",
      contactNumber: "",
      panNumber: "",
      aadhaarNumber: "",
      emailAddress: "",
    },
    validationSchema: yup.object().shape({
      firstName: yup.string().required("First Name is required"),
      lastName: yup.string().required("Last Name is required"),
      dob: yup.string().required("Date of birth is required"),
      aadhaarNumber: yup
        .string()
        .matches(
          /^[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}$/,
          "Aadhaar Number must be a 12-digit number starting from 2-9"
        )
        .required("Aadhaar Number is required"),
      panNumber: yup
        .string()
        .matches(
          /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
          "PAN Number must be in the format ABCDE1234F"
        )
        .required("PAN Number is required"),
      emailAddress: yup
        .string()
        .email("Please enter a valid email address")
        .required("Email Address is required"),
      contactNumber: yup
        .string()
        .required("Contact Number is required")
        .test(
          "is-valid-phone",
          "Contact Number is invalid",
          (value, context) => {
            const { contactNumber, countryCode } = context.parent;
            if (!value || !countryCode) return false;
            return isValidPhoneNumberWithRules(countryCode, contactNumber);
          }
        ),
      countryCode: yup.string().required("Country code is required"), // Validate country code if needed
    }),
  });

  useEffect(() => {
    setApplicantObject(`applicant${applicantNo}`);
    otherDetailFormik.resetForm();
    if (applicantData[`applicant${applicantNo}`] !== null) {
      otherDetailFormik.setValues({
        ...applicantData[`applicant${applicantNo}`]?.personalDetails,
        ...applicantData[`applicant${applicantNo}`]?.contactDetails,
      });
      otherDetailFormik.setFieldValue(
        "countryCode",
        applicantData[`applicant${applicantNo}`]?.contactDetails?.countryCode
      );
      otherDetailFormik.setFieldValue(
        "contactNumber",
        applicantData[`applicant${applicantNo}`]?.contactDetails?.contactNumber
      );
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData, applicantNo]);

  useEffect(() => {
    if (applicantData?.fillingAs === applicantNo) {
      otherDetailFormik.setFieldValue(
        "firstName",
        applicantData[`applicant${applicantNo}`]?.personalDetails?.firstName ||
          enquiryDetails?.data?.Cname ||
          ""
      );
      otherDetailFormik.setFieldValue(
        "lastName",
        applicantData[`applicant${applicantNo}`]?.personalDetails?.lastName ||
          enquiryDetails?.data?.X1st_Applicant_Last_Name ||
          ""
      );

      if (
        applicantData?.relationBtApplicants === "Spouse" &&
        applicantNo === applicantData?.fillingAs
      ) {
        otherDetailFormik.setFieldValue(
          "dob",
          applicantData[`applicant1`]?.personalDetails?.spouseDob || ""
        );
      }
      if (
        applicantData?.relationBtApplicants === "Spouse" &&
        applicantNo === applicantData?.fillingAs
      ) {
        otherDetailFormik.setFieldValue(
          "dob",
          applicantData[`applicant1`]?.personalDetails?.spouseDob || ""
        );
      }
    }
    if (
      EOIBooking &&
      applicantData[`applicant${applicantNo}`]?.personalDetails?.firstName ===
        null &&
      applicantData[`applicant${applicantNo}`]?.personalDetails?.lastName ===
        null &&
      applicantData[`applicant${applicantNo}`]?.personalDetails
        ?.motherTongue === null &&
      applicantData[`applicant${applicantNo}`]?.personalDetails?.dob === null &&
      applicantData[`applicant${applicantNo}`]?.contactDetails
        ?.contactNumber === null &&
      applicantData[`applicant${applicantNo}`]?.contactDetails
        ?.aadhaarNumber === null &&
      applicantData[`applicant${applicantNo}`]?.contactDetails
        ?.alternateCountryCode === null &&
      applicantData[`applicant${applicantNo}`]?.contactDetails?.countryCode ===
        null &&
      applicantData[`applicant${applicantNo}`]?.contactDetails?.emailAddress ===
        null &&
      applicantData[`applicant${applicantNo}`]?.contactDetails?.panNumber ===
        null
    ) {
      otherDetailFormik.resetForm();
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    EOIBooking,
    applicantData,
    applicantNo,
    enquiryDetails?.data?.Cname,
    enquiryDetails?.data?.X1st_Applicant_Last_Name,
  ]);

  useEffect(() => {
    otherDetailFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherDetailFormik?.values]);

  const handleMobileNumberAndEmail = () => {
    if (applicantData?.noOfApplicants) {
      for (let i = 1; i <= applicantData?.noOfApplicants; i++) {
        if (applicantData[`applicant${i}`]?.contactDetails !== null) {
          if (applicantNo !== i) {
            if (
              applicantData[`applicant${i}`]?.contactDetails?.contactNumber ===
              otherDetailFormik?.values?.contactNumber
            ) {
              setSnackBarconfig({
                open: true,
                message:
                  "This contact number is already associated with another applicant",
                severity: "error",
              });
              return false;
            } else if (
              applicantData[`applicant${i}`]?.contactDetails?.emailAddress ===
              otherDetailFormik?.values?.emailAddress
            ) {
              setSnackBarconfig({
                open: true,
                message:
                  "This email is already associated with another applicant",
                severity: "error",
              });
              return false;
            } else if (
              applicantData[`applicant${i}`]?.contactDetails?.aadhaarNumber ===
              otherDetailFormik?.values?.aadhaarNumber
            ) {
              setSnackBarconfig({
                open: true,
                message:
                  "This Aadhaar Number is already associated with another applicant",
                severity: "error",
              });
              return false;
            } else if (
              applicantData[
                `applicant${i}`
              ]?.contactDetails?.panNumber?.toLowerCase() ===
              otherDetailFormik?.values?.panNumber?.toLowerCase()
            ) {
              setSnackBarconfig({
                open: true,
                message:
                  "This PAN Number is already associated with another applicant",
                severity: "error",
              });
              return false;
            } else {
              if (i === applicantData?.noOfApplicants) {
                return true;
              }
            }
          } else {
            if (i === applicantData?.noOfApplicants) {
              return true;
            }
          }
        } else {
          return true;
        }
      }
    }
  };

  // Helper function to create the other applicant details payload
  const createOtherApplicantPayload = (saveForLaterFlag = false) => {
    return {
      applicantNumber: parseInt(applicantNo),
      saveForLater: saveForLaterFlag,
      personalDetails: {
        firstName: otherDetailFormik?.values?.firstName,
        lastName: otherDetailFormik?.values?.lastName,
        dob: otherDetailFormik?.values?.dob,
      },
      contactDetails: {
        panNumber: otherDetailFormik?.values?.panNumber?.toUpperCase(),
        aadhaarNumber: otherDetailFormik?.values?.aadhaarNumber,
        countryCode: otherDetailFormik?.values?.countryCode,
        contactNumber: otherDetailFormik?.values?.contactNumber,
        emailAddress: otherDetailFormik?.values?.emailAddress,
      },
    };
  };

  const handleSaveForm = async (type) => {
    if (
      Object.keys(otherDetailFormik.errors).length === 0 &&
      handleMobileNumberAndEmail()
    ) {
      const payload = {
        ...createOtherApplicantPayload(type === "saveForLater"),
        lastStep:activeStep+1,
      };

      await dispatch(
        updateApplicantDetailsForm({
          path: `${route.UPDATE_VOUCHER_APPLICANT_DETAILS}/${voucherId}`,
          payload: payload,
          opportunityId: voucherId,
        })
      )
        .unwrap()
        .then((res) => {
          if (res?.status === 200) {
            let completedSteps = {
              ...stepsCompleted,
              [activeStep]: true,
            };
            dispatch(setEditPreviewApplicant(false));
            dispatch(setStepsCompleted(completedSteps));
            dispatch(setApplicantActiveStep(0));
            dispatch(setActiveStep(activeStep + 1));
          }
        });
    }
  };
  const saveAndPreview = async (type) => {
    if (
      Object.keys(otherDetailFormik.errors).length === 0 &&
      handleMobileNumberAndEmail()
    ) {
      const payload = {
        ...createOtherApplicantPayload(type === "saveForLater"),
        lastStep:
          applicantData?.lastStep > activeStep
            ? applicantData?.lastStep
            : activeStep,
      };

      await dispatch(
        updateApplicantDetailsForm({
          path: `${route.UPDATE_VOUCHER_APPLICANT_DETAILS}/${voucherId}`,
          payload: payload,
          opportunityId: voucherId,
        })
      )
        .unwrap()
        .then((res) => {
          if (res?.status === 200) {
            dispatch(setEditPreviewApplicant(false));
            history.push(`/customer-preview/${voucherId}`);
            let completedSteps = {
              ...stepsCompleted,
              [activeStep]: true,
            };
            dispatch(setStepsCompleted(completedSteps));
            dispatch(setApplicantActiveStep(0));
          }
        });
    }
  };

  const handlePrevious = () => {
    dispatch(setActiveStep(activeStep - 1));
  };

  const handleSnackbarClose = () => {
    setSnackBarconfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);

    if (!applicantData[`applicant${applicantNo}`]) {
      dispatch(setApplicantTotal(applicantNo - 1));
      return;
    }
    try {
      await dispatch(
        deleteApplicantDetailsForm({
          voucherId: voucherId,
          applicantNo: applicantNo,
          previousStep: activeStep - 1,
        })
      ).unwrap();
      setDeleteDialogOpen(false);
      setSnackBarconfig({
        open: true,
        message: `Applicant ${applicantNo} deleted successfully`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting applicant:", error);
      setSnackBarconfig({
        open: true,
        message: "Failed to delete applicant. Please try again.",
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleAddApplicant = async (type) => {
    if (
      Object.keys(otherDetailFormik.errors).length === 0 &&
      handleMobileNumberAndEmail()
    ) {
      const payload = {
        ...createOtherApplicantPayload(type === "saveForLater"),
        lastStep: activeStep+1,
      };

      await dispatch(
        updateApplicantDetailsForm({
          path: `${route.UPDATE_VOUCHER_APPLICANT_DETAILS}/${voucherId}`,
          payload: payload,
          opportunityId: voucherId,
        })
      )
        .unwrap()
        .then((res) => {
          if (res?.status === 200) {
            let completedSteps = {
              ...stepsCompleted,
              [activeStep]: true,
            };
            dispatch(setApplicantTotal(applicantNo + 1));
            dispatch(setStepsCompleted(completedSteps));
            dispatch(setApplicantActiveStep(0));
            dispatch(setActiveStep(activeStep + 1));
          }
        });
    }
  };

  return (
    <div>
      {/* Applicant Details Title with Delete Button */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography
          sx={{
            color: "#1C252E",
            fontSize: "16px",
            fontWeight: "600",
            lineHeight: "36px",
            textAlign: "center",
            flex: 1,
          }}
        >
          {applicantNo === 1 ? "Primary" : `${applicantNo}`}
          {applicantNo !== 1 && (
            <sup>{APPLICANT_NUMBER_WORD[applicantNo - 1]}</sup>
          )}{" "}
          Applicant Details
        </Typography>
        {applicantNo !== 1 && applicantNo === applicantTotal && (
          <Button
            onClick={handleDeleteClick}
            startIcon={
              <img
                src={DeleteIcon}
                alt="delete"
                style={{ width: 20, height: 20 }}
              />
            }
            sx={{
              color: "#d32f2f",
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#ffebee",
              },
            }}
          >
            Delete Applicant
          </Button>
        )}
      </Box>
      <form
        onSubmit={otherDetailFormik.handleSubmit}
        onReset={otherDetailFormik.handleReset}
        noValidate
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <TextFieldInput
              field={{
                name: "firstName",
                label: "First Name",
                placeholder: "Enter First Name",
                required: true,
                readonly: false,
                handleOnChange: (e) => {
                  const value = e.target.value;
                  const newValue = value?.replace(/[^a-zA-Z]/g, "");
                  otherDetailFormik.setFieldValue("firstName", newValue);
                },
              }}
              formik={otherDetailFormik}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <TextFieldInput
              field={{
                name: "lastName",
                label: "Last Name",
                placeholder: "Enter Last Name",
                required: true,
                readonly: false,
                handleOnChange: (e) => {
                  const value = e.target.value;
                  const newValue = value?.replace(/[^a-zA-Z]/g, "");
                  otherDetailFormik.setFieldValue("lastName", newValue);
                },
              }}
              formik={otherDetailFormik}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <CustomDatePicker
              label="Date Of Birth"
              value={otherDetailFormik?.values?.dob}
              onChange={otherDetailFormik.handleChange}
              name="dob"
              maxDate={new Date()}
              minDate={new Date(DATE_START_YEAR, 0, 1)}
              error={
                otherDetailFormik.touched.dob &&
                Boolean(otherDetailFormik.errors.dob)
              }
              helperText={
                otherDetailFormik.touched.dob && otherDetailFormik.errors.dob
              }
              required={true}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <TextFieldInput
              field={{
                name: "emailAddress",
                label: "Email",
                placeholder: "Enter Email",
                required: true,
                readonly: false,
              }}
              formik={otherDetailFormik}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>

            <RHFPhoneInput
              name="contactNumber"
              countryCodeName="countryCode"
              label="Contact Number"
              country={otherDetailFormik?.values?.countryCode || "IN"}
              formik={otherDetailFormik}
              placeholder="Enter Contact Number"
              required
              showDialCode={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <TextFieldInput
              field={{
                name: "panNumber",
                label: "PAN Number",
                required: true,
                readonly: false,
                placeholder: "Enter PAN Number",
              }}
              formik={otherDetailFormik}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <TextFieldInput
              field={{
                name: "aadhaarNumber",
                label: "Aadhaar Number",
                required: true,
                readonly: false,
                placeholder: "Enter Aadhaar Number",
                maxLength: 12,
              }}
              formik={otherDetailFormik}
            />
          </Grid>
          <AddApplicantButton
            applicantNo={applicantNo}
            handleAddApplicant={handleAddApplicant}
            totalApplicants={applicantData?.noOfApplicants || applicantTotal}
            isOtherApplicant={
              applicantData?.noOfApplicants === 2 && applicantTotal === 3
                ? true
                : false
            }
          />
        </Grid>

        <CommonButtonVoucherForm
          handlePrevious={handlePrevious}
          handleNext={handleSaveForm}
          saveAndPreview={saveAndPreview}
          applicantNo={applicantNo}
          saveDraftData={{
            ...createOtherApplicantPayload(true),
            lastStep:
              applicantData?.lastStep > activeStep
                ? applicantData?.lastStep
                : activeStep,
          }}
        />
      </form>
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Applicant"
        message={`Are you sure you want to delete ${applicantNo}${
          APPLICANT_NUMBER_WORD[applicantNo - 1]
        } Applicant? This action cannot be undone and all data for this applicant will be permanently removed.`}
        applicantNo={applicantNo}
        loading={isDeleting}
      />
    </div>
  );
};

export default OtherApplicantDetails;
